import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, CheckCircle2, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Phase1Unterlagen = () => {
  const unterlagen = [
    { titel: 'Mietvertrag + alle Nachträge', details: 'Inkl. Indexierung, Optionen, Konkurrenzschutz, Instandhaltung', pflicht: true },
    { titel: 'Betriebskostenabrechnungen', details: 'Letzte 3 Jahre, Umlage-Aufstellung', pflicht: true },
    { titel: 'Grundbuchauszug', details: 'Nicht älter als 3 Monate, inkl. Abt. II + III', pflicht: true },
    { titel: 'Flurkarte + Lagepläne', details: 'Mit Parkplätzen, Anlieferung, Grenzen', pflicht: true },
    { titel: 'Flächenpläne', details: 'Verkaufsfläche nach gif/MF-G berechnet', pflicht: true },
    { titel: 'Energieausweis', details: 'Verbrauch oder Bedarf, gültig', pflicht: true },
    { titel: 'Baugenehmigung', details: 'Original oder begl. Kopie', pflicht: true },
    { titel: 'Mieteingangs-Nachweise', details: 'Letzte 12 Monate, keine Rückstände', pflicht: true },
    { titel: 'Fotos', details: 'Außen, Innen, Parkplätze, Umgebung', pflicht: false },
    { titel: 'Versicherungsnachweis', details: 'Gebäude, Haftpflicht', pflicht: false }
  ];

  const verantwortlichkeiten = [
    { wer: 'Eigentümer', aufgaben: ['Unterlagen zusammenstellen', 'Objektzugang ermöglichen', 'Mieter vorinformieren (diskret)', 'Steuerberater einbinden'] },
    { wer: 'RetailInvest', aufgaben: ['Checkliste bereitstellen', 'Unterlagen auf Vollständigkeit prüfen', 'WAULT berechnen', 'Erste Werteinschätzung', 'Verkaufsstrategie vorschlagen'] },
    { wer: 'Steuerberater', aufgaben: ['Spekulationsfrist prüfen', 'Steuerliche Verkaufsplanung', 'Optimierungsmöglichkeiten aufzeigen'] }
  ];

  const typischeProbleme = [
    { problem: 'Nachträge fehlen oder sind nicht auffindbar', loesung: 'Bei Mietverwalter/Vermieter-Archiv nachfragen, ggf. Mieter um Kopie bitten' },
    { problem: 'Grundbuch hat Lasten/Grundschulden', loesung: 'Mit Bank klären ob Ablösung bei Verkauf möglich, transparent an Käufer kommunizieren' },
    { problem: 'Flächenberechnung unklar', loesung: 'Sachverständigen beauftragen (gif/MF-G Standard), kostet 500-1.500 €' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Phase 1: Unterlagen & Vorbereitung – Verkaufsprozess Handelsimmobilie" description="Detaillierte Anleitung zu Phase 1: Welche Unterlagen brauchen Sie? Checkliste, Verantwortlichkeiten, Zeitrahmen." url="/ablauf/phase-1-unterlagen" />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/ablauf" className="text-sm hover:underline mb-4 inline-block" style={{ color: 'var(--navy-600)' }}>← Zurück zur Übersicht</Link>
          
          <div className="flex items-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #D4A853 0%, #F0C870 100%)' }}>
              <span className="text-4xl font-bold text-white">01</span>
            </div>
            <div>
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-2"></div>
              <h1 className="text-4xl font-bold" style={{ color: 'var(--navy-900)' }}>Unterlagen & Vorbereitung</h1>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="h-5 w-5" style={{ color: 'var(--gold-600)' }} />
                <p className="text-lg font-semibold" style={{ color: 'var(--gray-700)' }}>Dauer: 2-4 Wochen</p>
              </div>
            </div>
          </div>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Warum ist diese Phase so wichtig?</h2>
            <p className="text-lg mb-6 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
              Vollständige und korrekte Unterlagen sind die Basis für einen erfolgreichen Verkauf. Käufer (besonders institutionelle) 
              prüfen akribisch - fehlende Dokumente führen zu Verzögerungen, Nachverhandlungen oder sogar Abbruch. 
              <strong> Gut vorbereitete Objekte verkaufen sich 30-40% schneller</strong> und erzielen bessere Preise.
            </p>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Unterlagen-Checkliste</h2>
            <div className="space-y-4">
              {unterlagen.map((u, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <CheckCircle2 className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: u.pflicht ? 'var(--gold-600)' : 'var(--navy-400)' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold" style={{ color: 'var(--navy-800)' }}>{u.titel}</h3>
                      {u.pflicht && (
                        <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                          Pflicht
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-600)' }}>{u.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-8" style={{ color: 'var(--navy-800)' }}>Verantwortlichkeiten</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {verantwortlichkeiten.map((v, i) => (
                <div key={i} className="p-6 rounded-lg" style={{ backgroundColor: i === 1 ? 'var(--gold-100)' : 'var(--navy-50)' }}>
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>{v.wer}</h3>
                  <ul className="space-y-3">
                    {v.aufgaben.map((a, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--gray-700)' }}>
                        <span className="font-bold" style={{ color: 'var(--gold-600)' }}>•</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass-card p-10 mb-8">
            <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>Typische Probleme & Lösungen</h2>
            <div className="space-y-6">
              {typischeProbleme.map((item, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-red-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                      <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Problem:</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--gray-800)' }}>{item.problem}</p>
                  </div>
                  <div>
                    <h4 className="font-bold mb-2" style={{ color: 'var(--success-600)' }}>Lösung:</h4>
                    <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.loesung}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex gap-4">
            <Link to="/ablauf" className="flex-1">
              <Button variant="outline" className="w-full font-semibold py-6" style={{ borderColor: 'var(--navy-600)', color: 'var(--navy-600)' }}>
                ← Zurück zur Übersicht
              </Button>
            </Link>
            <Link to="/ablauf/phase-2-bewertung" className="flex-1">
              <Button className="w-full btn-gold-gradient text-white border-0 font-semibold py-6">
                Weiter zu Phase 2: Bewertung
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Phase1Unterlagen;
